import { Spin } from 'antd';
import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { ErrorBoundary } from './components/ErrorBoundary';
import { RequireRole } from './components/RequireRole';
import { AdminLayout } from './layouts/AdminLayout';
import { useAuthStore } from './stores/auth.store';

// Lazy-loaded pages
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const LoginPage = lazy(() => import('./pages/login/LoginPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));

// M1 — Product
const ProductListPage = lazy(() => import('./pages/m1-product/ProductListPage'));
const ProductDetailPage = lazy(() => import('./pages/m1-product/ProductDetailPage'));
const ProductEditPage = lazy(() => import('./pages/m1-product/ProductEditPage'));
const DocumentListPage = lazy(() => import('./pages/m1-product/DocumentListPage'));
const ImportPage = lazy(() => import('./pages/m1-product/ImportPage'));
const ProductBatchPage = lazy(() => import('./pages/m1-product/ProductBatchPage'));
const ParamStandardPage = lazy(() => import('./pages/m1-product/ParamStandardPage'));

// M2 — Supplier
const SupplierListPage = lazy(() => import('./pages/m2-supplier/SupplierListPage'));
const SupplierApplicationsPage = lazy(() => import('./pages/m2-supplier/SupplierApplicationsPage'));
const SupplierDetailPage = lazy(() => import('./pages/m2-supplier/SupplierDetailPage'));
const SupplierEditPage = lazy(() => import('./pages/m2-supplier/SupplierEditPage'));
const SubmissionListPage = lazy(() => import('./pages/m2-supplier/SubmissionListPage'));
const ExtractionReviewPage = lazy(() => import('./pages/m2-supplier/ExtractionReviewPage'));
const SupplierDashboardPage = lazy(() => import('./pages/m2-supplier/SupplierDashboardPage'));
const PurchaseOrderListPage = lazy(() => import('./pages/m2-supplier/PurchaseOrderListPage'));
const PurchaseOrderEditPage = lazy(() => import('./pages/m2-supplier/PurchaseOrderEditPage'));
const SupplierBatchPage = lazy(() => import('./pages/m2-supplier/SupplierBatchPage'));
const SupplierUpdateRequestsPage = lazy(() => import('./pages/m2-supplier/SupplierUpdateRequestsPage'));

// M3 — Website Content
const BlogListPage = lazy(() => import('./pages/m3-website/BlogListPage'));
const BlogEditPage = lazy(() => import('./pages/m3-website/BlogEditPage'));
const BlogDetailPage = lazy(() => import('./pages/m3-website/BlogDetailPage'));
const SolutionListPage = lazy(() => import('./pages/m3-website/SolutionListPage'));
const SolutionEditPage = lazy(() => import('./pages/m3-website/SolutionEditPage'));
const SolutionDetailPage = lazy(() => import('./pages/m3-website/SolutionDetailPage'));

// M3 Extended — Academy, Careers, Contact, RFQ, Subscribers
const CourseListPage = lazy(() => import('./pages/m3-website/CourseListPage'));
const CourseEditPage = lazy(() => import('./pages/m3-website/CourseEditPage'));
const JobListPage = lazy(() => import('./pages/m3-website/JobListPage'));
const JobEditPage = lazy(() => import('./pages/m3-website/JobEditPage'));
const JobApplicationListPage = lazy(() => import('./pages/m3-website/JobApplicationListPage'));
const ContactMessageListPage = lazy(() => import('./pages/m3-website/ContactMessageListPage'));
const SubscriberListPage = lazy(() => import('./pages/m3-website/SubscriberListPage'));
const RfqListPage = lazy(() => import('./pages/m3-website/RfqListPage'));
const LiveChatPage = lazy(() => import('./pages/m3-website/LiveChatPage'));

// M4 — Video
const VideoProjectListPage = lazy(() => import('./pages/m4-video/VideoProjectListPage'));
const VideoProjectDetailPage = lazy(() => import('./pages/m4-video/VideoProjectDetailPage'));
const VideoProjectEditPage = lazy(() => import('./pages/m4-video/VideoProjectEditPage'));
const VideoTemplateListPage = lazy(() => import('./pages/m4-video/VideoTemplateListPage'));
const RenderQueuePage = lazy(() => import('./pages/m4-video/RenderQueuePage'));
const VideoSubtitlePage = lazy(() => import('./pages/m4-video/VideoSubtitlePage'));
const VideoClipPage = lazy(() => import('./pages/m4-video/VideoClipPage'));

// M5 — Social
const SocialAccountListPage = lazy(() => import('./pages/m5-social/SocialAccountListPage'));
const SocialAccountEditPage = lazy(() => import('./pages/m5-social/SocialAccountEditPage'));
const SocialPostListPage = lazy(() => import('./pages/m5-social/SocialPostListPage'));
const SocialPostEditPage = lazy(() => import('./pages/m5-social/SocialPostEditPage'));
const SocialAnalyticsPage = lazy(() => import('./pages/m5-social/SocialAnalyticsPage'));

// M6 — CRM
const CustomerListPage = lazy(() => import('./pages/m6-crm/CustomerListPage'));
const CustomerEditPage = lazy(() => import('./pages/m6-crm/CustomerEditPage'));
const CustomerDetailPage = lazy(() => import('./pages/m6-crm/CustomerDetailPage'));
const InquiryListPage = lazy(() => import('./pages/m6-crm/InquiryListPage'));
const InquiryEditPage = lazy(() => import('./pages/m6-crm/InquiryEditPage'));
const InquiryDetailPage = lazy(() => import('./pages/m6-crm/InquiryDetailPage'));
const FollowUpListPage = lazy(() => import('./pages/m6-crm/FollowUpListPage'));
const EmailTemplateListPage = lazy(() => import('./pages/m6-crm/EmailTemplateListPage'));
const EmailTemplateEditPage = lazy(() => import('./pages/m6-crm/EmailTemplateEditPage'));
const EmailLogListPage = lazy(() => import('./pages/m6-crm/EmailLogListPage'));
const EmailApprovalQueuePage = lazy(() => import('./pages/m6-crm/EmailApprovalQueuePage'));
const CustomerBatchPage = lazy(() => import('./pages/m6-crm/CustomerBatchPage'));
const CustomerMapPage = lazy(() => import('./pages/m6-crm/CustomerMapPage'));
const CheckinPage = lazy(() => import('./pages/m6-crm/CheckinPage'));
const VisitCheckinListPage = lazy(() => import('./pages/m6-crm/VisitCheckinListPage'));
const PipelineKanbanPage = lazy(() => import('./pages/m6-crm/PipelineKanbanPage'));
const ReminderPage = lazy(() => import('./pages/m6-crm/ReminderPage'));
// M6 Quotes
const QuoteListPage = lazy(() => import('./pages/m6-crm/QuoteListPage'));
const QuoteEditPage = lazy(() => import('./pages/m6-crm/QuoteEditPage'));

// M6 McKay-66 expansion
const ContactProfilePage = lazy(() => import('./pages/m6-crm/ContactProfilePage'));
const CompanyListPage = lazy(() => import('./pages/m6-crm/CompanyListPage'));
const CompanyDetailPage = lazy(() => import('./pages/m6-crm/CompanyDetailPage'));
const CompanyEditPage = lazy(() => import('./pages/m6-crm/CompanyEditPage'));
const AiModelConfigPage = lazy(() => import('./pages/m6-crm/AiModelConfigPage'));
const SmartInputPage = lazy(() => import('./pages/m6-crm/SmartInputPage'));

// M6 Customer Pool
const CustomerPublicSeaPage = lazy(() => import('./pages/m6-crm/CustomerPublicSeaPage'));
const CustomerPoolSettingsPage = lazy(() => import('./pages/m6-crm/CustomerPoolSettingsPage'));

// M6 Security, Compliance & Operations
const TwoFactorAuthPage = lazy(() => import('./pages/m6-crm/TwoFactorAuthPage'));
const IpWhitelistPage = lazy(() => import('./pages/m6-crm/IpWhitelistPage'));
const AiQualityPage = lazy(() => import('./pages/m6-crm/AiQualityPage'));
const PiplCompliancePage = lazy(() => import('./pages/m6-crm/PiplCompliancePage'));
const ReportSchedulePage = lazy(() => import('./pages/m6-crm/ReportSchedulePage'));

// M6 Pipeline expansion
const LeadListPage = lazy(() => import('./pages/m6-crm/LeadListPage'));
const LeadEditPage = lazy(() => import('./pages/m6-crm/LeadEditPage'));
const OpportunityListPage = lazy(() => import('./pages/m6-crm/OpportunityListPage'));
const OpportunityEditPage = lazy(() => import('./pages/m6-crm/OpportunityEditPage'));
const ContractListPage = lazy(() => import('./pages/m6-crm/ContractListPage'));
const ContractEditPage = lazy(() => import('./pages/m6-crm/ContractEditPage'));
const InvoiceListPage = lazy(() => import('./pages/m6-crm/InvoiceListPage'));
const InvoiceEditPage = lazy(() => import('./pages/m6-crm/InvoiceEditPage'));
const PaymentListPage = lazy(() => import('./pages/m6-crm/PaymentListPage'));
const TaskListPage = lazy(() => import('./pages/m6-crm/TaskListPage'));
const BiAnalyticsPage = lazy(() => import('./pages/m6-crm/BiAnalyticsPage'));
const EmailInboxPage = lazy(() => import('./pages/m6-crm/EmailInboxPage'));
const CrmProductListPage = lazy(() => import('./pages/m6-crm/CrmProductListPage'));

// M7 — Sales Orders
const SalesOrderListPage = lazy(() => import('./pages/m7-order/SalesOrderListPage'));
const SalesOrderDetailPage = lazy(() => import('./pages/m7-order/SalesOrderDetailPage'));

// M8 — Quality Control
const QcInspectionListPage = lazy(() => import('./pages/m8-qc/QcInspectionListPage'));
const QcInspectionDetailPage = lazy(() => import('./pages/m8-qc/QcInspectionDetailPage'));

// M9 — Logistics & Shipping
const ShipmentListPage = lazy(() => import('./pages/m9-logistics/ShipmentListPage'));
const ShipmentDetailPage = lazy(() => import('./pages/m9-logistics/ShipmentDetailPage'));

// M10 — Digital SDR
const DigitalSdrDashboard = lazy(() => import('./pages/m10-sdr/DigitalSdrDashboard'));
const AiDepartmentPage = lazy(() => import('./pages/m10-sdr/AiDepartmentPage'));
const LeadReviewPage = lazy(() => import('./pages/m10-sdr/LeadReviewPage'));
const SearchStrategyPage = lazy(() => import('./pages/m10-sdr/SearchStrategyPage'));
const SdrRunsPage = lazy(() => import('./pages/m10-sdr/SdrRunsPage'));

// M11 — Inventory Management
const InventoryListPage = lazy(() => import('./pages/m11-inventory/InventoryList'));
const GoodsReceiptPage = lazy(() => import('./pages/m11-inventory/GoodsReceiptPage'));
const BomMatchPage = lazy(() => import('./pages/m11-inventory/BomMatchPage'));
const InternalProjectPage = lazy(() => import('./pages/m11-inventory/InternalProjectPage'));
const IssueNotePage = lazy(() => import('./pages/m11-inventory/IssueNotePage'));
const BinLocationPage = lazy(() => import('./pages/m11-inventory/BinLocationPage'));
const BinManagementPage = lazy(() => import('./pages/m11-inventory/BinManagementPage'));
const AssetManagementPage = lazy(() => import('./pages/m11-inventory/AssetManagementPage'));
const AssetTracePage = lazy(() => import('./pages/m11-inventory/AssetTracePage'));

// M12 — Finance (AR/AP) & Payment Gateway
const FinanceDashboardPage = lazy(() => import('./pages/m12-finance/FinanceDashboard'));
const ARListPage = lazy(() => import('./pages/m12-finance/ARList'));
const APListPage = lazy(() => import('./pages/m12-finance/APList'));
const PaymentGatewayPage = lazy(() => import('./pages/m12-finance/PaymentGateway'));

// M13 — OCR Document Processing
const OcrUploadPage = lazy(() => import('./pages/m13-ocr/OcrUploadPage'));

// M14 — Logistics Tracking (17track)
const TrackingListPage = lazy(() => import('./pages/m14-tracking/TrackingList'));

// Outbound — AI calling
const OutboundCallResultsPage = lazy(() => import('./pages/outbound/CallResultsPage'));
const OutboundLeadReviewPage = lazy(() => import('./pages/outbound/LeadReviewPage'));
const OutboundCampaignPage = lazy(() => import('./pages/outbound/CampaignPage'));
const OutboundDialogueSimPage = lazy(() => import('./pages/outbound/DialogueSimPage'));
const OutboundCollectPage = lazy(() => import('./pages/outbound/CollectPage'));

// System — User Management
const UserListPage = lazy(() => import('./pages/users/UserListPage'));
const UserEditPage = lazy(() => import('./pages/users/UserEditPage'));

const Loading: React.FC = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spin size="large" tip="加载中..." />
  </div>
);

/** Route guard: redirects to login if not authenticated */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  const location = useLocation();
  if (!isAuthenticated) {
    const redirect = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirect)}`} replace />;
  }
  return <>{children}</>;
};

export const App: React.FC = () => {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <Suspense fallback={<Loading />}>
        <Routes>
          {/* Public route: Login */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/asset/:token" element={<AssetTracePage />} />

          {/* Protected routes: Admin Dashboard */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />

            {/* M1 — Product */}
            <Route path="m1/products" element={<ProductListPage />} />
            <Route path="m1/products/new" element={<ProductEditPage />} />
            <Route path="m1/products/:id" element={<ProductDetailPage />} />
            <Route path="m1/products/:id/edit" element={<ProductEditPage />} />
            <Route path="m1/documents" element={<DocumentListPage />} />
            <Route path="m1/import" element={<ImportPage />} />
            <Route path="m1/products/batch" element={<ProductBatchPage />} />
            <Route path="m1/param-standards" element={<ParamStandardPage />} />

            {/* M2 — Supplier */}
            <Route path="m2/dashboard" element={<SupplierDashboardPage />} />
            <Route path="m2/supplier-applications" element={<SupplierApplicationsPage />} />
            <Route path="m2/suppliers" element={<SupplierListPage />} />
            <Route path="m2/extraction-review" element={<ExtractionReviewPage />} />
            <Route path="m2/suppliers/new" element={<SupplierEditPage />} />
            <Route path="m2/suppliers/:id" element={<SupplierDetailPage />} />
            <Route path="m2/suppliers/:id/edit" element={<SupplierEditPage />} />
            <Route path="m2/submissions" element={<SubmissionListPage />} />
            <Route path="m2/purchase-orders" element={<PurchaseOrderListPage />} />
            <Route path="m2/purchase-orders/new" element={<PurchaseOrderEditPage />} />
            <Route path="m2/purchase-orders/:id" element={<PurchaseOrderEditPage />} />
            <Route path="m2/suppliers/batch" element={<SupplierBatchPage />} />
            <Route path="m2/update-requests" element={<SupplierUpdateRequestsPage />} />

            {/* M3 — Website Content */}
            <Route path="m3/blogs" element={<BlogListPage />} />
            <Route path="m3/blogs/new" element={<BlogEditPage />} />
            <Route path="m3/blogs/:id" element={<BlogDetailPage />} />
            <Route path="m3/blogs/:id/edit" element={<BlogEditPage />} />
            <Route path="m3/solutions" element={<SolutionListPage />} />
            <Route path="m3/solutions/new" element={<SolutionEditPage />} />
            <Route path="m3/solutions/:id" element={<SolutionDetailPage />} />
            <Route path="m3/solutions/:id/edit" element={<SolutionEditPage />} />

            {/* M3 Extended — Academy, Careers, Contact, RFQ, Subscribers */}
            <Route path="m3/courses" element={<CourseListPage />} />
            <Route path="m3/courses/new" element={<CourseEditPage />} />
            <Route path="m3/courses/:id/edit" element={<CourseEditPage />} />
            <Route path="m3/jobs" element={<JobListPage />} />
            <Route path="m3/jobs/new" element={<JobEditPage />} />
            <Route path="m3/jobs/:id/edit" element={<JobEditPage />} />
            <Route path="m3/jobs/:jobId/applications" element={<JobApplicationListPage />} />
            <Route path="m3/contacts" element={<ContactMessageListPage />} />
            <Route path="m3/ai-contacts" element={<ContactMessageListPage source="ai_chat" />} />
            <Route path="m3/subscribers" element={<SubscriberListPage />} />
            <Route path="m3/rfq" element={<RfqListPage />} />
            <Route path="m3/live-chat" element={<LiveChatPage />} />

            {/* M4 — Video */}
            <Route path="m4/projects" element={<VideoProjectListPage />} />
            <Route path="m4/projects/new" element={<VideoProjectEditPage />} />
            <Route path="m4/projects/:id" element={<VideoProjectDetailPage />} />
            <Route path="m4/projects/:id/edit" element={<VideoProjectEditPage />} />
            <Route path="m4/templates" element={<VideoTemplateListPage />} />
            <Route path="m4/render-queue" element={<RenderQueuePage />} />
            <Route path="m4/videos/:id/subtitles" element={<VideoSubtitlePage />} />
            <Route path="m4/videos/:id/clips" element={<VideoClipPage />} />

            {/* M5 — Social */}
            <Route path="m5/accounts" element={<SocialAccountListPage />} />
            <Route path="m5/accounts/new" element={<SocialAccountEditPage />} />
            <Route path="m5/accounts/:id/edit" element={<SocialAccountEditPage />} />
            <Route path="m5/posts" element={<SocialPostListPage />} />
            <Route path="m5/posts/new" element={<SocialPostEditPage />} />
            <Route path="m5/posts/:id/edit" element={<SocialPostEditPage />} />
            <Route path="m5/analytics" element={<SocialAnalyticsPage />} />

            {/* M6 — CRM */}
            <Route path="m6/pipeline" element={<PipelineKanbanPage />} />
            <Route path="m6/customers" element={<CustomerListPage />} />
            <Route path="m6/customers/new" element={<CustomerEditPage />} />
            <Route path="m6/customers/batch" element={<CustomerBatchPage />} />
            <Route path="m6/customers/:id" element={<CustomerDetailPage />} />
            <Route path="m6/customers/:id/edit" element={<CustomerEditPage />} />
            <Route path="m6/inquiries" element={<InquiryListPage />} />
            <Route path="m6/inquiries/new" element={<InquiryEditPage />} />
            <Route path="m6/inquiries/:id" element={<InquiryDetailPage />} />
            <Route path="m6/inquiries/:id/edit" element={<InquiryEditPage />} />
            <Route path="m6/followups" element={<FollowUpListPage />} />
            <Route path="m6/reminders" element={<ReminderPage />} />
            <Route path="m6/email-templates" element={<EmailTemplateListPage />} />
            <Route path="m6/email-templates/new" element={<EmailTemplateEditPage />} />
            <Route path="m6/email-templates/:id/edit" element={<EmailTemplateEditPage />} />
            <Route path="m6/email-logs" element={<EmailLogListPage />} />
            <Route path="m6/customer-map" element={<CustomerMapPage />} />
            <Route path="m6/checkin" element={<CheckinPage />} />
            <Route path="m6/visit-checkins" element={<RequireRole roles={['ADMIN']}><VisitCheckinListPage /></RequireRole>} />
            {/* M6 Quotes */}
            <Route path="m6/quotes" element={<QuoteListPage />} />
            <Route path="m6/quotes/new" element={<QuoteEditPage />} />
            <Route path="m6/quotes/:id" element={<QuoteEditPage />} />
            <Route path="m6/quotes/:id/edit" element={<QuoteEditPage />} />

            {/* M6 McKay-66 expansion */}
            <Route path="m6/contacts/:id" element={<ContactProfilePage />} />
            <Route path="m6/companies" element={<CompanyListPage />} />
            <Route path="m6/companies/new" element={<CompanyEditPage />} />
            <Route path="m6/companies/:id" element={<CompanyDetailPage />} />
            <Route path="m6/companies/:id/edit" element={<CompanyEditPage />} />
            <Route path="m6/ai-models" element={<AiModelConfigPage />} />
            <Route path="m6/smart-input" element={<SmartInputPage />} />

            {/* M6 Customer Pool */}
            <Route path="m6/customers/public-sea" element={<CustomerPublicSeaPage />} />
            <Route path="m6/pool-settings" element={<RequireRole roles={['ADMIN']}><CustomerPoolSettingsPage /></RequireRole>} />

            {/* M6 Security & Compliance */}
            <Route path="m6/2fa" element={<TwoFactorAuthPage />} />
            <Route path="m6/ip-whitelist" element={<IpWhitelistPage />} />
            <Route path="m6/pipl" element={<PiplCompliancePage />} />

            {/* M6 AI & Operations */}
            <Route path="m6/ai-quality" element={<AiQualityPage />} />
            <Route path="m6/report-schedules" element={<ReportSchedulePage />} />

            {/* M6 Pipeline expansion */}
            <Route path="m6/leads" element={<LeadListPage />} />
            <Route path="m6/leads/new" element={<LeadEditPage />} />
            <Route path="m6/leads/:id" element={<LeadEditPage />} />
            <Route path="m6/opportunities" element={<OpportunityListPage />} />
            <Route path="m6/opportunities/new" element={<OpportunityEditPage />} />
            <Route path="m6/opportunities/:id" element={<OpportunityEditPage />} />
            <Route path="m6/contracts" element={<ContractListPage />} />
            <Route path="m6/contracts/new" element={<ContractEditPage />} />
            <Route path="m6/contracts/:id" element={<ContractEditPage />} />
            <Route path="m6/invoices" element={<InvoiceListPage />} />
            <Route path="m6/invoices/new" element={<InvoiceEditPage />} />
            <Route path="m6/invoices/:id" element={<InvoiceEditPage />} />
            <Route path="m6/payments" element={<PaymentListPage />} />
            <Route path="m6/tasks" element={<TaskListPage />} />
            <Route path="m6/analytics" element={<BiAnalyticsPage />} />
            <Route path="m6/crm-products" element={<CrmProductListPage />} />
            <Route path="m6/email" element={<EmailInboxPage />} />
            <Route path="m6/email-approvals" element={<EmailApprovalQueuePage />} />

            {/* M7 — Sales Orders */}
            <Route path="m7/sales-orders" element={<SalesOrderListPage />} />
            <Route path="m7/sales-orders/:id" element={<SalesOrderDetailPage />} />

            {/* M8 — Quality Control */}
            <Route path="m8/inspections" element={<QcInspectionListPage />} />
            <Route path="m8/inspections/:id" element={<QcInspectionDetailPage />} />

            {/* M9 — Logistics */}
            <Route path="m9/shipments" element={<ShipmentListPage />} />
            <Route path="m9/shipments/:id" element={<ShipmentDetailPage />} />

            {/* M10 — Digital SDR */}
            <Route path="m10/sdr" element={<DigitalSdrDashboard />} />
            <Route path="m10/department" element={<AiDepartmentPage />} />
            <Route path="m10/sdr/leads" element={<LeadReviewPage />} />
            <Route path="m10/sdr/runs" element={<SdrRunsPage />} />
            <Route path="m10/strategies" element={<SearchStrategyPage />} />

            {/* M11 — Inventory Management */}
            <Route path="m11/inventory" element={<InventoryListPage />} />
            <Route path="m11/goods-receipts" element={<GoodsReceiptPage />} />
            <Route path="m11/bom" element={<BomMatchPage />} />
            <Route path="m11/projects" element={<InternalProjectPage />} />
            <Route path="m11/issue-notes" element={<IssueNotePage />} />
            <Route path="m11/locator" element={<BinLocationPage />} />
            <Route path="m11/bin/:binCode" element={<BinLocationPage />} />
            <Route path="m11/bin-settings" element={<RequireRole roles={['ADMIN']}><BinManagementPage /></RequireRole>} />
            <Route path="m11/assets" element={<RequireRole roles={['ADMIN', 'ASSET_MANAGER']}><AssetManagementPage /></RequireRole>} />

            {/* M12 — Finance */}
            <Route path="m12/finance" element={<FinanceDashboardPage />} />
            <Route path="m12/ar" element={<ARListPage />} />
            <Route path="m12/ap" element={<APListPage />} />
            <Route path="m12/payment-gateway" element={<PaymentGatewayPage />} />

            {/* M13 — OCR */}
            <Route path="m13/ocr" element={<OcrUploadPage />} />

            {/* M14 — Logistics Tracking */}
            <Route path="m14/tracking" element={<TrackingListPage />} />

            {/* Outbound — AI calling */}
            <Route path="outbound/collect" element={<OutboundCollectPage />} />
            <Route path="outbound/review" element={<OutboundLeadReviewPage />} />
            <Route path="outbound/campaigns" element={<OutboundCampaignPage />} />
            <Route path="outbound/dialogue" element={<OutboundDialogueSimPage />} />
            <Route path="outbound/calls" element={<OutboundCallResultsPage />} />

            {/* System — User Management (Admin only) */}
            <Route path="system/users" element={<RequireRole roles={['ADMIN']}><UserListPage /></RequireRole>} />
            <Route path="system/users/new" element={<RequireRole roles={['ADMIN']}><UserEditPage /></RequireRole>} />
            <Route path="system/users/:id/edit" element={<RequireRole roles={['ADMIN']}><UserEditPage /></RequireRole>} />
          </Route>

          {/* Catch-all: 404 page */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
    </ErrorBoundary>
  );
};
